import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  Button,
  type Column,
  type StatusType,
  type ToggleOption,
} from '@themes/aegix';
import { useEventGuardians } from '../../../hooks/queries/useReunification';
import { useFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import type { GuardianReunification } from '../../../types/ReunificationTypes';
import { GuardianDetailDrawer } from './GuardianDetailDrawer';
import { AddEventGuardianDialog } from '../../reunification/AddEventGuardianDialog';

const toggleOptions: ToggleOption[] = [
  { value: 'all', label: 'All' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'verified', label: 'Verified' },
];

interface GuardianRow {
  [key: string]: unknown;
  id: string;
  name: string;
  status: StatusType;
  status_label: string;
  student_count: number;
  arrived: string;
  verified: string;
  raw_status: string;
  // Editable fields surfaced from the list serializer so the
  // detail drawer can render + PATCH without a second fetch.
  is_pre_verified: boolean;
  raw_verification_method: string;
  raw_id_type: string;
  raw_authorized_students: string[];
  raw_authorized_student_names: string[];
}

function mapGuardianStatus(status: string): StatusType {
  if (['verified', 'reunified', 'exited', 'released'].includes(status))
    return 'active';
  if (['denied', 'not_arrived'].includes(status)) return 'inactive';
  return 'pending';
}

const columns: Column<GuardianRow>[] = [
  { key: 'name', label: 'Name', sortable: true, width: '180px' },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: '120px',
    render: (row) => <StatusBadge status={row.status} label={row.status_label} />,
  },
  {
    key: 'student_count',
    label: 'Students',
    sortable: false,
    width: '100px',
  },
  { key: 'arrived', label: 'Arrived', sortable: true, width: '140px' },
  { key: 'verified', label: 'Verified', sortable: true, width: '140px' },
];

function transformGuardians(guardians: GuardianReunification[]): GuardianRow[] {
  return guardians.map((g) => ({
    id: g.public_id,
    name: g.guardian_name,
    status: mapGuardianStatus(g.status),
    status_label: g.status_display,
    student_count: g.student_count,
    arrived: g.arrived_at
      ? new Date(g.arrived_at).toLocaleString()
      : '---',
    verified: g.verified_at
      ? new Date(g.verified_at).toLocaleString()
      : '---',
    raw_status: g.status,
    is_pre_verified: !!g.is_pre_verified,
    raw_verification_method: g.verification_method ?? '',
    raw_id_type: g.id_type ?? '',
    raw_authorized_students: g.authorized_students ?? [],
    raw_authorized_student_names: g.authorized_student_names ?? [],
  }));
}

function collectExistingGuardianRosterPersonIds(
  guardians: GuardianReunification[] | undefined,
): number[] {
  if (!guardians) return [];
  const ids = guardians
    .map((g) => g.roster_person)
    .filter((id): id is number => Number.isFinite(id));
  return Array.from(new Set(ids));
}

interface EventGuardiansPanelProps {
  eventId: string;
}

import { useTranslation } from 'react-i18next';

export function EventGuardiansPanel({ eventId }: EventGuardiansPanelProps) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [selectedGuardian, setSelectedGuardian] = useState<GuardianRow>();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Surface the Add button only when the reunification feature is
  // enabled for this site. The backend's `CanOperateGuardianVerification`
  // permission still gates the API and 403s any direct call regardless of
  // this flag. A dedicated `REUNIFICATION_MANAGE_GUARDIANS` feature is
  // tracked as a follow-up under ASM-278 to give districts finer-grained
  // control without disabling the whole reunification module.
  const canManageGuardian = useFeature(FeatureCode.REUNIFICATION);

  const { data, isLoading, isFetching, error } = useEventGuardians(eventId, {
    search: searchValue || undefined,
    status:
      statusFilter === 'all'
        ? undefined
        : (statusFilter as 'arrived' | 'verified'),
    page: currentPage,
    page_size: pageSize,
    ordering: sortColumn
      ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
      : undefined,
  });

  const guardians = data?.results ? transformGuardians(data.results) : [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const existingGuardianRosterPersonIds = useMemo(
    () => collectExistingGuardianRosterPersonIds(data?.results),
    [data?.results],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleRowClick = useCallback((row: GuardianRow) => {
    setSelectedGuardian(row);
  }, []);

  return (
    <>
      <Flex align="center" gap={3} pb={2}>
        <Box flex={1}>
          <TableOptions
            toggleOptions={toggleOptions}
            activeToggle={statusFilter}
            onToggleChange={(v: string) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
            searchValue={searchValue}
            onSearchChange={(v) => {
              setSearchValue(v);
              setCurrentPage(1);
            }}
          />
        </Box>
        {canManageGuardian && (
          <Button
            visual="primary"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            data-e2e="event-guardian-add-button"
          >
            Add guardian
          </Button>
        )}
      </Flex>

      <Box pb={3}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : 'Failed to load guardians'}
            </Text>
          </Center>
        ) : guardians.length === 0 ? (
          <Center py={12}>
            {isFetching ? (
              <Spinner size="lg" color="button.primary" />
            ) : (
              <Text color="text.secondary">{t('events.noGuardiansFound', 'No guardians found')}</Text>
            )}
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={guardians}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </Box>

      <GuardianDetailDrawer
        isOpen={!!selectedGuardian}
        onClose={() => setSelectedGuardian(undefined)}
        eventId={eventId}
        guardian={selectedGuardian}
      />

      <AddEventGuardianDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        eventId={eventId}
        existingGuardianRosterPersonIds={existingGuardianRosterPersonIds}
      />
    </>
  );
}
